import Link from "next/link";
import { Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { formatName } from "src/utils/formatName";
import { Category } from "src/types/Category.d";

type CategoryCardSize = "l" | "m" | "s";

export type CategoryCardColor = "tone.white" | "tone.sand";

const FONT_SIZES = {
  l: 2,
  m: 1,
  s: 0,
};

export default function CategoryCard({
  category,
  size = "l",
  link,
  color = "tone.sand",
  hasPadding = true,
  imageAspectRatio,
  onClick,
}: {
  category: Category;
  size?: CategoryCardSize;
  link: string;
  color?: CategoryCardColor;
  hasPadding?: boolean;
  imageAspectRatio?: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <Link href={link} prefetch={false} onClick={onClick}>
      <Flex
        justifyContent={imageAspectRatio ? "flex-start" : "center"}
        alignItems="center"
        flexDirection="column"
        sx={{
          height: "100%",
          transition: "opacity .3s ease-in-out",
          cursor: "pointer",
          ":hover": { opacity: 0.6 },
        }}
        pt={hasPadding ? 3 : 0}
        pb={hasPadding ? 3 : 0}
      >
        <Box
          px={hasPadding ? "space16" : "space0"}
          sx={{
            textAlign: "center",
            ...(imageAspectRatio
              ? {
                  width: "100%",
                  aspectRatio: imageAspectRatio,
                  minHeight: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
              : { flex: 1, minHeight: 0 }),
          }}
        >
          <img
            alt={
              "name" in category
                ? formatName(category.name)
                : formatName(category.title)
            }
            src={category.thumbnail}
            style={
              imageAspectRatio
                ? { maxWidth: "100%", maxHeight: "100%" }
                : { maxHeight: "100%" }
            }
          />
        </Box>
        <Box sx={{ textAlign: "center", wordBreak: "break-word" }}>
          <Text
            fontSize={[0, 0, 0, FONT_SIZES[size]]}
            color="tone.black"
            fontWeight={600}
            pt={
              hasPadding
                ? ["space0", "space0", "space8", "space8", "space8"]
                : "space8"
            }
          >
            {"name" in category
              ? formatName(category.name)
              : formatName(category.title)}
          </Text>
        </Box>
      </Flex>
    </Link>
  );
}
